import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Plus, Edit2, Search, Save, X, Tag } from 'lucide-react';

const Offers = () => {
    const { authFetch } = useAuth();
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({ name: '', id_tarifa: '', slogan: '', precio: 0, active: true });

    const [isCreating, setIsCreating] = useState(false);
    const [createForm, setCreateForm] = useState({ name: '', id_tarifa: '', slogan: '', precio: 0.00, active: true });

    useEffect(() => {
        fetchOffers();
        // eslint-disable-next-line
    }, []);

    const fetchOffers = async () => {
        setLoading(true);
        try {
            const res = await authFetch('/api/backoffice/offers');
            if (res.ok) {
                const data = await res.json();
                setOffers(data);
            }
        } catch (error) {
            console.error("Error fetching offers:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async () => {
        if (!createForm.name.trim() || !createForm.id_tarifa.trim()) return;
        try {
            const res = await authFetch('/api/backoffice/offers', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...createForm, precio: parseFloat(createForm.precio) || 0 })
            });
            if (res.ok) {
                const newOffer = await res.json();
                setOffers([newOffer, ...offers]);
                setIsCreating(false);
                setCreateForm({ name: '', id_tarifa: '', slogan: '', precio: 0, active: true });
            }
        } catch (error) {
            console.error("Error creating offer:", error);
        }
    };

    const handleUpdate = async (id) => {
        if (!editForm.name.trim() || !editForm.id_tarifa.trim()) return;
        try {
            const res = await authFetch(`/api/backoffice/offers/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...editForm, precio: parseFloat(editForm.precio) || 0 })
            });
            if (res.ok) {
                const updatedOffer = await res.json();
                setOffers(offers.map(o => o.id === id ? updatedOffer : o));
                setEditingId(null);
            }
        } catch (error) {
            console.error("Error updating offer:", error);
        }
    };

    const startEdit = (offer) => {
        setEditingId(offer.id);
        setEditForm({
            name: offer.name,
            id_tarifa: offer.id_tarifa,
            slogan: offer.slogan || '',
            precio: offer.precio || 0,
            active: offer.active
        });
    };

    const filteredOffers = offers.filter(o =>
        o.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (o.id_tarifa && o.id_tarifa.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center bg-slate-900/50 p-6 rounded-2xl border border-slate-800">
                <div>
                    <h1 className="text-2xl font-bold">Ofertas</h1>
                    <p className="text-slate-400 text-sm">Gestiona el catálogo de ofertas y precios.</p>
                </div>
                {!isCreating && (
                    <button
                        onClick={() => setIsCreating(true)}
                        className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-xl font-medium hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20"
                    >
                        <Plus size={18} />
                        <span>Nueva Oferta</span>
                    </button>
                )}
            </div>

            {/* Quick Create Form */}
            {isCreating && (
                <div className="bg-slate-800/80 border border-primary/30 p-6 rounded-2xl animate-in slide-in-from-top-4 duration-300 shadow-xl">
                    <div className="grid grid-cols-12 gap-4 mb-4">
                        <div className="col-span-12 md:col-span-4">
                            <label className="block text-xs font-medium text-slate-400 mb-1 uppercase tracking-wider">Nombre Oferta</label>
                            <input
                                type="text" placeholder="Ej. Tarifa Luz 2.0"
                                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-2 text-slate-100 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                                value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} autoFocus
                            />
                        </div>
                        <div className="col-span-12 md:col-span-3">
                            <label className="block text-xs font-medium text-slate-400 mb-1 uppercase tracking-wider">ID Tarifa (API)</label>
                            <input
                                type="text" placeholder="ID en sistema origen"
                                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-2 text-slate-100 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary font-mono text-sm"
                                value={createForm.id_tarifa} onChange={(e) => setCreateForm({ ...createForm, id_tarifa: e.target.value })}
                            />
                        </div>
                        <div className="col-span-12 md:col-span-3">
                            <label className="block text-xs font-medium text-slate-400 mb-1 uppercase tracking-wider">Precio (€)</label>
                            <div className="relative">
                                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-500 font-bold">€</span>
                                <input
                                    type="number" step="0.001" placeholder="0.000"
                                    className="w-full bg-slate-900 border border-slate-700 rounded-xl pl-8 pr-4 py-2 text-slate-100 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary text-lg font-bold"
                                    value={createForm.precio} onChange={(e) => setCreateForm({ ...createForm, precio: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="col-span-12 md:col-span-2 flex items-end">
                            <label className="flex items-center justify-center gap-2 cursor-pointer text-sm text-slate-300 bg-slate-900/50 w-full px-4 py-2.5 rounded-xl border border-slate-700 h-[42px]">
                                <input type="checkbox" className="accent-primary w-4 h-4 cursor-pointer rounded" checked={createForm.active} onChange={(e) => setCreateForm({ ...createForm, active: e.target.checked })} />
                                <span>Activa</span>
                            </label>
                        </div>
                        <div className="col-span-12">
                            <label className="block text-xs font-medium text-slate-400 mb-1 uppercase tracking-wider">Slogan Preferencia</label>
                            <input
                                type="text" placeholder="Ej. 🔥 Mejor Precio Garantizado"
                                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-2 text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                                value={createForm.slogan} onChange={(e) => setCreateForm({ ...createForm, slogan: e.target.value })}
                            />
                        </div>
                    </div>
                    <div className="flex justify-end gap-3 pt-2 border-t border-slate-700/50 mt-2">
                        <button onClick={() => setIsCreating(false)} className="px-5 py-2 text-slate-400 hover:text-slate-200 hover:bg-slate-700/50 rounded-xl transition-colors">Cancelar</button>
                        <button
                            onClick={handleCreate} disabled={!createForm.name.trim() || !createForm.id_tarifa.trim()}
                            className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-2 rounded-xl font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
                        >
                            <Save size={18} /> Guardar Oferta
                        </button>
                    </div>
                </div>
            )}

            <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
                {/* Search */}
                <div className="p-4 border-b border-slate-800 bg-slate-950/50">
                    <div className="relative max-w-md">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search size={16} className="text-slate-500" />
                        </div>
                        <input
                            type="text" placeholder="Buscar por nombre o ID tarifa..."
                            className="w-full bg-slate-900 border border-slate-800 rounded-lg pl-10 pr-4 py-2 text-sm text-slate-200 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all"
                            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {/* Grid Layout Headers */}
                <div className="hidden md:grid grid-cols-12 gap-4 p-4 border-b border-slate-800 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-900/80">
                    <div className="col-span-4 pl-2">Detalle Oferta</div>
                    <div className="col-span-4 text-center">Slogan Preferencia</div>
                    <div className="col-span-2 text-center">Precio</div>
                    <div className="col-span-2 text-right pr-4">Acciones</div>
                </div>

                <div className="divide-y divide-slate-800/50 bg-[#0B1120]">
                    {loading ? (
                        <div className="p-8 text-center text-slate-500 animate-pulse">Cargando catálogo...</div>
                    ) : filteredOffers.length === 0 ? (
                        <div className="p-8 text-center text-slate-500">No se encontraron ofertas.</div>
                    ) : (
                        filteredOffers.map(offer => {
                            const isEditing = editingId === offer.id;
                            return (
                                <div key={offer.id} className="grid grid-cols-1 md:grid-cols-12 gap-y-4 gap-x-4 p-5 hover:bg-slate-800/40 transition-colors group items-center">
                                    {/* Column 1: Info (Name + ID) */}
                                    <div className="col-span-1 md:col-span-4 flex items-start gap-4">
                                        <div className={`mt-1 flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center border transition-colors shadow-sm ${offer.active && !isEditing ? 'bg-primary/10 text-primary border-primary/20 shadow-primary/10' : 'bg-slate-800 text-slate-500 border-slate-700'}`}>
                                            <Tag size={18} />
                                        </div>
                                        <div className="flex-1 w-full flex flex-col gap-2">
                                            {isEditing ? (
                                                <>
                                                    <input type="text" className="bg-slate-950 border border-primary/50 rounded w-full px-3 py-1.5 focus:outline-none text-sm font-bold" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} placeholder="Nombre" />
                                                    <input type="text" className="bg-slate-950 border border-primary/50 rounded w-full px-3 py-1.5 focus:outline-none font-mono text-xs text-slate-400" value={editForm.id_tarifa} onChange={(e) => setEditForm({ ...editForm, id_tarifa: e.target.value })} placeholder="ID Tarifa" />
                                                </>
                                            ) : (
                                                <>
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-bold text-slate-100 truncate">{offer.name}</span>
                                                        {!offer.active && <span className="bg-slate-800 text-slate-400 text-[10px] uppercase px-2 py-0.5 rounded border border-slate-700">Inactiva</span>}
                                                    </div>
                                                    <div className="font-mono text-xs text-slate-500 flex items-center gap-1">
                                                        <span>ID:</span> <span className="text-slate-400 bg-slate-800/50 px-1.5 py-0.5 rounded truncate max-w-[150px] inline-block">{offer.id_tarifa}</span>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* Column 2: Slogan */}
                                    <div className="col-span-1 md:col-span-4 flex items-center justify-start md:justify-center">
                                        {isEditing ? (
                                            <input type="text" className="bg-slate-950 border border-primary/50 rounded w-full text-center px-3 py-1.5 focus:outline-none text-sm text-primary" value={editForm.slogan} onChange={(e) => setEditForm({ ...editForm, slogan: e.target.value })} placeholder="Slogan..." />
                                        ) : (
                                            <span className="text-sm font-medium text-amber-400/90 bg-amber-500/10 px-3 py-1.5 rounded-lg border border-amber-500/20 shadow-sm text-center line-clamp-2">
                                                {offer.slogan || <span className="opacity-0">-</span>}
                                            </span>
                                        )}
                                    </div>

                                    {/* Column 3: Precio (Very Large Visual) */}
                                    <div className="col-span-1 md:col-span-2 flex items-center justify-start md:justify-center">
                                        {isEditing ? (
                                            <div className="flex items-center w-28">
                                                <span className="bg-slate-800 px-2 py-1.5 rounded-l text-slate-400 border border-r-0 border-primary/50">€</span>
                                                <input type="number" step="0.001" className="bg-slate-950 border border-primary/50 rounded-r w-full px-2 py-1.5 focus:outline-none font-bold text-center" value={editForm.precio} onChange={(e) => setEditForm({ ...editForm, precio: e.target.value })} />
                                            </div>
                                        ) : (
                                            <div className="font-sans">
                                                <span className="text-3xl font-black text-white tracking-tighter drop-shadow-md">
                                                    {Number(offer.precio || 0).toFixed(3).replace('.', ',')}
                                                </span>
                                                <span className="text-sm font-bold text-slate-500 ml-1">€</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Column 4: Actions & Active state */}
                                    <div className="col-span-1 md:col-span-2 flex flex-col md:flex-row items-end md:items-center justify-between md:justify-end gap-3 mt-4 md:mt-0">
                                        {isEditing ? (
                                            <div className="flex flex-col gap-2 w-full md:w-auto items-end">
                                                <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-300">
                                                    <input type="checkbox" className="accent-primary w-3 h-3 cursor-pointer rounded" checked={editForm.active} onChange={(e) => setEditForm({ ...editForm, active: e.target.checked })} />
                                                    <span>Activa</span>
                                                </label>
                                                <div className="flex gap-1">
                                                    <button onClick={() => handleUpdate(offer.id)} className="p-1.5 bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 rounded shadow-sm border border-emerald-500/30">
                                                        <Save size={16} />
                                                    </button>
                                                    <button onClick={() => setEditingId(null)} className="p-1.5 bg-slate-700/50 text-slate-300 hover:bg-slate-700 rounded shadow-sm border border-slate-600">
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => startEdit(offer)}
                                                className="p-2 text-slate-500 hover:text-white hover:bg-primary rounded-lg transition-all shadow-sm border border-transparent hover:border-primary/50 mt-1"
                                                title="Editar"
                                            >
                                                <Edit2 size={16} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};

export default Offers;
